import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';
import { readReturnTo } from '../lib/returnTo.js';
import { useBranding } from '../lib/branding.js';
import { useCapabilities } from '../lib/capabilities.js';
import { api, ApiError } from '../lib/api.js';
import type { SsoConnectionOption } from '../lib/types.js';
import { PasswordInput } from '../components/ui.js';
import { GoogleSignInButton } from '../components/GoogleSignInButton.js';

export function LoginScreen(): ReactNode {
  const { user, login, loginWithGoogle } = useAuth();
  const { branding } = useBranding();
  const caps = useCapabilities();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [sso, setSso] = useState<SsoConnectionOption[]>([]);

  useEffect(() => {
    void api.ssoOptions().then(setSso);
  }, []);

  useEffect(() => {
    if (user) navigate(readReturnTo('/displays'), { replace: true });
  }, [user, navigate]);

  async function signIn(attempt: () => Promise<void>): Promise<void> {
    setError(undefined);
    setBusy(true);
    try {
      await attempt();
      navigate(readReturnTo('/displays'), { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.fieldMessage() ?? err.message)
          : 'Could not reach the server.',
      );
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    void signIn(() => login(email, password, remember));
  }

  const googleClientId = caps?.googleClientId ?? null;

  // A fresh Akarya Box has no account yet: go straight to first-run setup.
  if (caps?.boxSetup) return <Navigate to="/setup" replace />;

  return (
    <div
      className="auth-wrap"
      style={
        branding.loginBackgroundUrl
          ? {
              backgroundImage: `url(${branding.loginBackgroundUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : undefined
      }
    >
      <form className="card card-pad auth-card stack" onSubmit={submit}>
        <div>
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.productName}
              style={{ maxHeight: 40, maxWidth: '70%', marginBottom: 4 }}
            />
          ) : (
            <h1 style={{ fontSize: 18 }}>{branding.productName}</h1>
          )}
          <p className="muted small" style={{ margin: '4px 0 0' }}>
            Admin console{caps ? ` · ${caps.tier} tier` : ''}
          </p>
        </div>
        {sso.length > 0 || googleClientId ? (
          <>
            <div className="stack small">
              {googleClientId ? (
                <GoogleSignInButton
                  clientId={googleClientId}
                  disabled={busy}
                  onCredential={(idToken) => void signIn(() => loginWithGoogle(idToken, remember))}
                  onError={setError}
                />
              ) : null}
              {sso.map((c) => (
                <a key={c.id} className="btn" href={api.ssoRedirectUrl(c.id)}>
                  Continue with {c.name}
                </a>
              ))}
            </div>
            <div className="row" style={{ alignItems: 'center', gap: 8 }}>
              <hr style={{ flex: 1 }} />
              <span className="muted small">or</span>
              <hr style={{ flex: 1 }} />
            </div>
          </>
        ) : null}
        <label className="field">
          Email
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </label>
        <label className="field">
          Password
          <PasswordInput
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
            required
          />
        </label>
        <div className="row small" style={{ justifyContent: 'space-between' }}>
          <label className="row">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              style={{ width: 'auto' }}
            />
            Keep me signed in
          </label>
          <Link to={email ? `/forgot-password?email=${encodeURIComponent(email)}` : '/forgot-password'}>
            Forgot password?
          </Link>
        </div>
        {error ? <div className="form-error">{error}</div> : null}
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        {caps?.signup ? (
          <p className="muted small" style={{ margin: 0, textAlign: 'center' }}>
            No account yet? <Link to="/signup">Create one</Link>
          </p>
        ) : null}
      </form>
    </div>
  );
}
